/**
 * Mode Switcher - Toggle between centralized, decentralized, and hybrid modes
 */

import { Monitor, Globe, GitMerge, ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { useDashboardStore } from "@/store";

type SystemMode = "centralized" | "decentralized" | "hybrid";

interface ModeOption {
  id: SystemMode;
  label: string;
  icon: any;
  description: string;
  color: string;
}

const MODES: ModeOption[] = [
  {
    id: "centralized",
    label: "Centralized",
    icon: Monitor,
    description: "Use local kernel for full control",
    color: "bg-blue-500",
  },
  {
    id: "decentralized",
    label: "Decentralized",
    icon: Globe,
    description: "Use Convex cloud for distributed operations",
    color: "bg-purple-500",
  },
  {
    id: "hybrid",
    label: "Hybrid",
    icon: GitMerge,
    description: "Auto-switch between backends",
    color: "bg-green-500",
  },
];

export function ModeSwitcher({ size = "default" }: { size?: "default" | "sm" | "lg" }) {
  const { systemMode, setSystemMode, health } = useDashboardStore();

  const currentMode = MODES.find((m) => m.id === systemMode) || MODES[2];

  // Check which modes are available based on backend health
  const availableModes = MODES.filter((mode) => {
    if (mode.id === "hybrid") return health?.kernel?.healthy || health?.convex?.healthy;
    if (mode.id === "centralized") return health?.kernel?.healthy;
    if (mode.id === "decentralized") return health?.convex?.healthy;
    return false;
  });

  const isSmall = size === "sm";
  const isLarge = size === "lg";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={cn(
          "flex items-center gap-2 rounded-lg transition-colors cursor-pointer",
          isSmall
            ? "px-2 py-1 text-xs hover:bg-[hsl(var(--muted))]"
            : isLarge
              ? "px-4 py-2 text-sm hover:bg-[hsl(var(--muted))]"
              : "px-3 py-1.5 text-xs hover:bg-[hsl(var(--muted))]"
        )}
      >
        <div
          className={cn(
            "flex h-4 items-center justify-center rounded",
            currentMode.color,
            isSmall ? "h-3 w-3" : isLarge ? "h-5 w-5" : "h-4 w-4"
          )}
        >
          <div className="h-1.5 w-1.5 rounded-full bg-white" />
        </div>
        {!isSmall && <span className="font-medium">{currentMode.label} Mode</span>}
        <ChevronDown className={cn("h-4 w-4", isSmall && "h-3 w-3")} />
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-56">
        {MODES.map((mode) => {
          const isAvailable = availableModes.some((m) => m.id === mode.id);
          const isActive = systemMode === mode.id;
          const Icon = mode.icon;

          return (
            <DropdownMenuItem
              key={mode.id}
              onClick={() => isAvailable && setSystemMode(mode.id)}
              disabled={!isAvailable}
              className={cn(
                "flex items-center gap-3 cursor-pointer",
                isActive && "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]",
                !isAvailable && "opacity-50 cursor-not-allowed"
              )}
            >
              <div
                className={cn(
                  "flex h-8 w-8 items-center justify-center rounded-lg",
                  isActive ? "bg-white/20" : "bg-[hsl(var(--muted))]"
                )}
              >
                <Icon className="h-4 w-4" />
              </div>
              <div className="flex-1">
                <div className="font-medium">{mode.label}</div>
                <div className="text-xs text-[hsl(var(--muted-foreground))]">{mode.description}</div>
              </div>
              {!isAvailable && <div className="text-xs text-[hsl(var(--destructive))]">Unavailable</div>}
              {isActive && <div className="flex h-2 w-2 rounded-full bg-white" />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Compact Mode Badge (for header)
 */
export function ModeBadge() {
  const { systemMode } = useDashboardStore();

  const mode = MODES.find((m) => m.id === systemMode) || MODES[2];
  const Icon = mode.icon;

  return (
    <div
      className={cn(
        "flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs font-medium",
        `${mode.color}/20`,
        mode.color.replace("bg-", "text-")
      )}
    >
      <Icon className="h-3.5 w-3.5" />
      <span>{mode.label}</span>
    </div>
  );
}
